import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { Allergen, DietaryRestriction } from '@sg/graphql-schema';
import {
  type DietaryPreferencesMachineImplementations,
  mergeRemoteAllergens,
  mergeRemoteDietaryRestrictions,
} from '@sg/shared-fe/features/menu';

import {
  GetDietaryPreferencesWithAllergensDocument,
  GetDietaryPreferencesDocument,
  type GetDietaryPreferencesWithAllergensQuery,
  type GetDietaryPreferencesWithAllergensQueryVariables,
  type GetDietaryPreferencesQuery,
  type GetDietaryPreferencesQueryVariables,
  useUpdateDietaryPreferencesWithAllergensMutation,
  useUpdateDietaryPreferencesMutation,
} from './graphql/CustomerDietaryPreferences.generated';
import { getDietaryPreferencesList } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useFetchDietaryPreferencesActor = () => {
  const client = useClient();
  const [, updatePreferencesWithRestrictions] = useUpdateDietaryPreferencesMutation();
  const [, updatePreferencesWithAllergens] = useUpdateDietaryPreferencesWithAllergensMutation();

  return useMemo<FetchCustomerDietaryPreferences>(
    () =>
      fromPromise(async (args) => {
        const { input } = args;
        const { preferences: existingPreferences, isAllergensEnabled } = input;

        // ─── Allergens ───────────────────────────────────────────────────────

        if (isAllergensEnabled) {
          const query = client.query<
            GetDietaryPreferencesWithAllergensQuery,
            GetDietaryPreferencesWithAllergensQueryVariables
          >;

          const response = await query(
            GetDietaryPreferencesWithAllergensDocument,
            {},
            { requestPolicy: 'network-only' },
          ).toPromise();

          const preferencesData = response.data?.getPreferences;

          if (!preferencesData) {
            throw new Error('Failed to fetch customer dietary preferences');
          }

          if (!preferencesData?.hasSyncedRestrictions) {
            const { error } = await updatePreferencesWithAllergens({
              input: {
                allergens: getDietaryPreferencesList(existingPreferences) as Allergen[],
              },
            });

            if (error) {
              throw new Error('Failed to sync local dietary preferences to remote');
            }

            return existingPreferences;
          }

          return mergeRemoteAllergens(preferencesData?.allergens ?? []);
        }

        // ─── Restrictions ────────────────────────────────────────────────────

        const query = client.query<GetDietaryPreferencesQuery, GetDietaryPreferencesQueryVariables>;

        const response = await query(
          GetDietaryPreferencesDocument,
          {},
          { requestPolicy: 'network-only' },
        ).toPromise();

        const preferencesData = response.data?.getPreferences;

        if (!preferencesData) {
          throw new Error('Failed to fetch customer dietary preferences');
        }

        if (!preferencesData?.hasSyncedRestrictions) {
          const { error } = await updatePreferencesWithRestrictions({
            input: {
              dietaryRestrictions: getDietaryPreferencesList(
                existingPreferences,
              ) as DietaryRestriction[],
            },
          });

          if (error) {
            throw new Error('Failed to sync local dietary preferences to remote');
          }

          return existingPreferences;
        }

        return mergeRemoteDietaryRestrictions(preferencesData?.dietaryRestrictions ?? []);
      }),
    [client.query, updatePreferencesWithAllergens, updatePreferencesWithRestrictions],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchCustomerDietaryPreferences = NonNullable<
  NonNullable<DietaryPreferencesMachineImplementations['actors']>['fetchCustomerDietaryPreferences']
>;
